import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

export default function MandateInstallmentInvoice() {
  const { mandateId, installmentId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    api
      .get(`/mandates/${mandateId}/installments/${installmentId}/details`)
      .then(({ data }) => {
        if (mounted) setInvoice(data);
      })
      .catch((e) => {
        if (mounted) {
          setError(e.response?.data?.detail || "Could not load invoice.");
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [mandateId, installmentId]);

  const downloadPdf = async () => {
    try {
      const response = await api.get(
        `/mandates/${mandateId}/installments/${installmentId}/invoice`,
        { responseType: "blob" }
      );

      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice?.invoice_no || "invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Could not download the PDF invoice.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080b12] p-8 text-center text-white/70">
        Loading invoice...
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-[#080b12] p-8 text-center text-white">
        <p>{error || "Invoice not found."}</p>
        <button className="mt-4 underline" onClick={() => window.history.back()}>
          Go back
        </button>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#080b12] px-4 py-8 text-white print:bg-white print:p-0 print:text-black">
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm"
            >
              <Printer size={16} /> Print
            </button>
            <button
              onClick={downloadPdf}
              className="inline-flex items-center gap-2 rounded-lg bg-[#fbce18] px-4 py-2 text-sm font-semibold text-black"
            >
              <Download size={16} /> Download PDF
            </button>
          </div>
        </div>

        <article className="rounded-2xl border border-white/10 bg-[#111722] p-6 shadow-xl sm:p-10 print:rounded-none print:border-0 print:bg-white print:shadow-none">
          <header className="flex flex-wrap justify-between gap-6 border-b border-white/10 pb-6 print:border-gray-300">
            <div className="flex items-center gap-3">
              {invoice.logo_url ? (
                <img
                  src={invoice.logo_url}
                  alt={invoice.mandate_title || "Mandate"}
                  className="max-h-16 max-w-36 rounded bg-white p-2 object-contain"
                />
              ) : (
                <div className="text-xl font-black tracking-wide">
                  <span className="text-[#fbce18] print:text-black">LOST</span>DRKC
                </div>
              )}
              <div>
                <p className="text-sm text-white/50 print:text-gray-600">
                  Payment Invoice
                </p>
                {invoice.mandate_title && (
                  <p className="mt-1 text-sm font-semibold">
                    {invoice.mandate_title}
                  </p>
                )}
              </div>
            </div>

            <div className="text-left sm:text-right">
              <div className="text-xs uppercase tracking-widest text-white/45 print:text-gray-500">
                Invoice Number
              </div>
              <div className="mt-1 font-semibold">
                {invoice.invoice_no || `INST-${installmentId}`}
              </div>
              <div className="mt-2 text-sm text-white/55 print:text-gray-600">
                {invoice.created_at
                  ? new Date(invoice.created_at).toLocaleDateString("en-IN")
                  : "—"}
              </div>
              {(() => {
                const status = String(invoice.status || "pending").toLowerCase();
                const paid = ["paid", "captured", "success", "completed"].includes(status);
                return (
                  <div className="mt-3">
                    <span
                      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        paid
                          ? "border-emerald-400/30 bg-emerald-400/15 text-emerald-300 print:border-green-600 print:bg-green-100 print:text-green-700"
                          : "border-amber-400/30 bg-amber-400/15 text-amber-300 print:border-yellow-600 print:bg-yellow-100 print:text-yellow-700"
                      }`}
                    >
                      {paid ? "Paid" : "Pending"}
                    </span>
                  </div>
                );
              })()}
            </div>
          </header>

          <section className="grid gap-6 py-7 sm:grid-cols-2">
            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/45 print:text-gray-500">
                Billed To
              </h2>
              <div className="font-semibold">{invoice.customer_name || "Customer"}</div>
              <div className="mt-1 text-sm text-white/60 print:text-gray-600">
                {invoice.customer_email || ""}
              </div>
            </div>

            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/45 print:text-gray-500">
                Invoice Details
              </h2>
              <div className="text-sm">
                <span className="text-white/50 print:text-gray-600">Mandate: </span>
                {invoice.mandate_title || invoice.mandate_id}
              </div>
              <div className="mt-1 text-sm">
                <span className="text-white/50 print:text-gray-600">Payment Method: </span>
                {invoice.payment_method || "UPI"}
              </div>
            </div>
          </section>

          <div className="overflow-hidden rounded-xl border border-white/10 print:border-gray-300">
            <div className="grid grid-cols-[1fr_auto] gap-4 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-wider print:bg-gray-100">
              <span>Description</span>
              <span>Amount</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-4 px-4 py-5">
              <div>
                <div className="font-medium">Mandate installment payment</div>
                <div className="mt-1 text-sm text-white/50 print:text-gray-600">
                  Installment ID: {invoice.installment_id || installmentId}
                </div>
              </div>
              <div className="font-semibold">{money(invoice.amount)}</div>
            </div>
          </div>

          <div className="ml-auto mt-6 max-w-sm space-y-3">
            <div className="flex justify-between text-sm text-white/60 print:text-gray-600">
              <span>Subtotal</span>
              <span>{money(invoice.amount)}</span>
            </div>
            <div className="flex justify-between border-t border-white/10 pt-3 text-lg font-bold print:border-gray-300">
              <span>Total</span>
              <span>{money(invoice.amount)}</span>
            </div>
          </div>

          <footer className="mt-10 border-t border-white/10 pt-5 text-center text-xs text-white/40 print:border-gray-300 print:text-gray-500">
            This invoice was generated electronically by LostDRKC Payments.
          </footer>
        </article>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </main>
  );
}
